/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ActionGroup } from "client-code/actions";
import type {
  Channel,
  TActionsParams,
  TCustomActionList,
  TPreviewServiceGetOfferPreviewParamsV4
} from "@tivocorp/data-domain-lib";
import type { AiringOffer } from "client-code/airing";

import { MetadataType } from "../enums";
import { GenericErrorCode, PlaybackError } from "../error";
import type {
  ILinearMetadataParams,
  IMetadata,
  IMetadataModel,
  TMetadataModelParams
} from "../interfaces";
import { Log, Diagnostics } from "../../logging";
import {
  ActionsInteractor,
  AiringOfferInteractor,
  PreviewInteractor
} from "../../interactor";

/**
 * Metadata model implementation for LINEAR (live TV) content.
 *
 * Handles fetching and storing metadata for live TV playback:
 * - AIRING: Current and next airing offers from airing service
 * - PREVIEW: Offer preview information from preview service
 * - ACTION_LIST: Action list for the current airing offer
 *
 * @implements {IMetadataModel}
 */
type AiringOfferRecord = Record<string, unknown>;

export class LinearMetadataModelImpl implements IMetadataModel {
  private static readonly TAG: string = "LinearMetadataModelImpl";

  private static readonly METHOD_FETCH_OFFER_PREVIEW: string =
    "fetchOfferPreview()";

  private static readonly METHOD_FETCH_ACTION_LIST: string =
    "fetchActionList()";

  /**
   * Default action groups to fetch for linear content.
   * Includes all relevant action types for live TV playback.
   */
  private static readonly DEFAULT_ACTION_GROUPS: ActionGroup[] = [
    ActionGroup.Watch,
    ActionGroup.ScheduleRecording,
    ActionGroup.ScheduleBookmark,
    ActionGroup.ScheduleOnepass,
    ActionGroup.Rent
  ];

  private mMetadata: IMetadata;

  private mError: PlaybackError;

  private mPreviewInteractor: PreviewInteractor;

  private mAiringInteractor: AiringOfferInteractor;

  private mActionsInteractor: ActionsInteractor;

  /**
   * Creates an instance of LinearMetadataModelImpl.
   * Services are lazily initialized when needed.
   */
  public constructor() {
    this.mMetadata = {};
    this.mError = new PlaybackError("", "");
    this.mPreviewInteractor = new PreviewInteractor();
    this.mAiringInteractor = new AiringOfferInteractor();
    this.mActionsInteractor = new ActionsInteractor();
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](LinearMetadataModelImpl.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      LinearMetadataModelImpl.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(
        LinearMetadataModelImpl.TAG,
        "stacktrace:,",
        optionalParams[0]
      );
    }
  }

  /**
   * Gets the aggregated metadata for the current content.
   * @returns The metadata object containing airing offers, previews, etc.
   */
  public getMetadata(): IMetadata {
    return this.mMetadata;
  }

  /**
   * Gets the current error from the last metadata operation.
   * @returns The PlaybackError if an error occurred.
   */
  public getError(): PlaybackError {
    return this.mError;
  }

  /**
   * Validates that this model can handle the given metadata type.
   * @param metadataType - The metadata type to validate
   * @returns True if the type is valid for LINEAR content
   */
  private isValidLinearMetadataType(
    metadataType: MetadataType
  ): metadataType is
    | MetadataType.AIRING
    | MetadataType.PREVIEW
    | MetadataType.ACTION_LIST {
    return (
      metadataType === MetadataType.AIRING ||
      metadataType === MetadataType.PREVIEW ||
      metadataType === MetadataType.ACTION_LIST
    );
  }

  /**
   * Type guard to check if params are LINEAR metadata params.
   * @param params - The metadata params to check
   * @returns True if params are LINEAR type
   */
  private isLinearParams(
    params: TMetadataModelParams
  ): params is ILinearMetadataParams {
    return (
      "channel" in params &&
      params.channel !== undefined &&
      this.isValidLinearMetadataType(params.metadataType)
    );
  }

  /**
   * Fetch metadata of a specific type for the current content.
   *
   * @param params - Discriminated union of metadata params by content type.
   * @returns Promise that resolves when metadata is loaded.
   * @throws PlaybackError if params are invalid or fetch fails
   */
  public async prepareMetadataByType(
    params: TMetadataModelParams
  ): Promise<void> {
    if (!this.isLinearParams(params)) {
      const errorMessage = "Invalid params for LinearMetadataModelImpl";
      this.log("error", "prepareMetadataByType(): ", errorMessage);
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        errorMessage,
        "Invalid Parameters",
        "The metadata parameters are not valid for linear content."
      );
      throw this.mError;
    }

    const { channel, metadataType, playerCurrentTime } = params;

    if (metadataType === MetadataType.AIRING) {
      await this.fetchAiringOffers(channel, playerCurrentTime);
    } else if (metadataType === MetadataType.PREVIEW) {
      await this.fetchOfferPreview(channel);
    } else if (MetadataType.ACTION_LIST) {
      await this.fetchActionList(channel);
    } else {
      // Type-safe exhaustive check
      this.handleUnknownMetadataType(metadataType as never);
    }
  }

  /**
   * Handles unknown metadata types (compile-time exhaustive check).
   * @param metadataType - The unknown metadata type
   */
  private handleUnknownMetadataType(metadataType: never): void {
    const errorMessage = `Unknown metadata type: ${metadataType}`;
    this.log("error", "handleUnknownMetadataType()", errorMessage);
    this.mError = new PlaybackError(
      GenericErrorCode.UNKNOWN_ERROR,
      errorMessage,
      "Unknown Metadata Type",
      "An unsupported metadata type was requested."
    );
    throw this.mError;
  }

  /**
   * Fetches current and next airing offers for the channel.
   *
   * @param channel - The channel to fetch airing offers for
   * @param playerCurrentTime - Optional player current time for boundary detection
   */
  private async fetchAiringOffers(
    channel: Channel,
    playerCurrentTime?: number
  ): Promise<void> {
    try {
      this.log("debug", `fetchAiringOffers() stationId: ${channel.stationId}`);

      const airingOffers: Array<AiringOffer> =
        await this.mAiringInteractor.fetchAiringOffers(
          channel.stationId || "",
          playerCurrentTime
        );

      this.mMetadata = {
        ...this.mMetadata,
        airingOffers: airingOffers as unknown as Array<Record<string, unknown>>
      };

      this.log(
        "info",
        `fetchAiringOffers() success, offers count: ${airingOffers.length}`
      );
    } catch (error) {
      this.log("error", "fetchAiringOffers()", error);
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "Failed to fetch airing offers",
        "Airing Information Unavailable",
        "Unable to retrieve program information for this channel."
      );
      throw this.mError;
    }
  }

  /**
   * Fetches offer preview for the current airing.
   * Requires airing offers to be fetched first.
   *
   * @param channel - The channel to fetch preview for
   */
  private async fetchOfferPreview(channel: Channel): Promise<void> {
    try {
      this.log("debug", `fetchOfferPreview() channelId: ${channel.channelId}`);

      // Get the current airing offer ID from metadata
      const currentAiringOffer = this.getCurrentAiringOffer();
      if (currentAiringOffer === null) {
        const errorMessage =
          "No current airing offer available. Fetch airing offers first.";
        this.log(
          "warn",
          LinearMetadataModelImpl.METHOD_FETCH_OFFER_PREVIEW,
          errorMessage
        );
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          errorMessage,
          "No Program Information",
          "Program information is not available for this channel."
        );
        throw this.mError;
      }

      const offerId = this.getOfferIdFromAiring(currentAiringOffer);
      if (offerId === null) {
        const errorMessage = "No offer ID found in current airing offer";
        this.log(
          "error",
          LinearMetadataModelImpl.METHOD_FETCH_OFFER_PREVIEW,
          errorMessage
        );
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          errorMessage,
          "Preview Unavailable",
          "Unable to retrieve preview for this program."
        );
        throw this.mError;
      }

      const previewParams: TPreviewServiceGetOfferPreviewParamsV4 = {
        offerId
      };

      const offerPreview = await this.mPreviewInteractor.fetchOfferPreviewV4(
        previewParams,
        false
      );

      this.mMetadata = {
        ...this.mMetadata,
        offerPreview: offerPreview
      };

      this.log(
        "info",
        LinearMetadataModelImpl.METHOD_FETCH_OFFER_PREVIEW + " success"
      );
    } catch (error) {
      // Only re-throw if it's already a PlaybackError we created
      if (error instanceof PlaybackError) {
        throw error;
      }
      this.log(
        "error",
        LinearMetadataModelImpl.METHOD_FETCH_OFFER_PREVIEW,
        error
      );
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "Failed to fetch offer preview",
        "Preview Unavailable",
        "Unable to retrieve preview information for this program."
      );
      throw this.mError;
    }
  }

  /**
   * Fetches action list for the current airing offer.
   * Requires airing offers to be fetched first to get the offerId.
   *
   * @param channel - The channel to fetch action list for
   */
  private async fetchActionList(channel: Channel): Promise<void> {
    try {
      this.log("debug", `fetchActionList() channelId: ${channel.channelId}`);

      // Get the current airing offer ID from metadata
      const currentAiringOffer = this.getCurrentAiringOffer();
      if (currentAiringOffer === null) {
        const errorMessage =
          "No current airing offer available. Fetch airing offers first.";
        this.log(
          "warn",
          LinearMetadataModelImpl.METHOD_FETCH_ACTION_LIST,
          errorMessage
        );
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          errorMessage,
          "No Program Information",
          "Program information is not available for this channel."
        );
        throw this.mError;
      }

      const offerId = this.getOfferIdFromAiring(currentAiringOffer);
      if (offerId === null) {
        const errorMessage = "No offer ID found in current airing offer";
        this.log(
          "error",
          LinearMetadataModelImpl.METHOD_FETCH_ACTION_LIST,
          errorMessage
        );
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          errorMessage,
          "Actions Unavailable",
          "Unable to retrieve actions for this program."
        );
        throw this.mError;
      }

      const actionsParams: TActionsParams = {
        offerId,
        groups: LinearMetadataModelImpl.DEFAULT_ACTION_GROUPS
      };

      const actionListResponse: TCustomActionList =
        await this.mActionsInteractor.fetchOfferActions(actionsParams, false);

      this.mMetadata = {
        ...this.mMetadata,
        actions: actionListResponse.actions
      };

      this.log(
        "info",
        LinearMetadataModelImpl.METHOD_FETCH_ACTION_LIST + " success"
      );
    } catch (error) {
      // Only re-throw if it's already a PlaybackError we created
      if (error instanceof PlaybackError) {
        throw error;
      }
      this.log(
        "error",
        LinearMetadataModelImpl.METHOD_FETCH_ACTION_LIST,
        error
      );
      this.mError = new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "Failed to fetch action list",
        "Actions Unavailable",
        "Unable to retrieve available actions for this program."
      );
      throw this.mError;
    }
  }

  /**
   * Gets the current airing offer from metadata.
   * @returns The current airing offer or null if not available
   */
  private getCurrentAiringOffer(): AiringOfferRecord | null {
    const airingOffers = this.mMetadata.airingOffers;
    if (
      airingOffers === undefined ||
      airingOffers === null ||
      airingOffers.length === 0
    ) {
      return null;
    }
    return airingOffers[0];
  }

  /**
   * Extracts the offer ID from an airing offer.
   * @param airingOffer - The airing offer to extract ID from
   * @returns The offer ID or null if not found
   */
  private getOfferIdFromAiring(airingOffer: AiringOfferRecord): string | null {
    // AiringOffer should have an offerId property
    // Using type assertion since the exact structure depends on the generated client
    const offer = airingOffer as { offerId?: string; id?: string };
    if (offer.offerId !== undefined && offer.offerId !== null) {
      return offer.offerId;
    }
    if (offer.id !== undefined && offer.id !== null) {
      return offer.id;
    }
    return null;
  }

  /**
   * Clears all stored metadata.
   * Call this when switching channels or stopping playback.
   */
  public clearMetadata(): void {
    this.mMetadata = {};
    this.mError = new PlaybackError("", "");
    this.log("debug", "clearMetadata() called");
  }
}
